import type { ComponentChildren } from 'preact'

export interface SegmentedToggleOption<T extends string> {
  value: T
  label: string
  /** Optional leading icon (e.g. a lucide-preact icon element). */
  icon?: ComponentChildren
  /** Optional trailing node (e.g. a count badge) rendered after the label. */
  badge?: ComponentChildren
}

export interface SegmentedToggleProps<T extends string> {
  options: SegmentedToggleOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Accessible label for the group (rendered via aria-label on the radiogroup container). */
  ariaLabel: string
  className?: string
}

/**
 * Compact two-or-more-option segmented control. Used for the Edit/Preview switch that appears in the
 * activation editor below the md breakpoint. Keyboard-operable: arrow keys move between segments, and
 * each segment is a real button so Enter/Space/Tab work natively. Exposed as a radiogroup for AT.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className = '',
}: SegmentedToggleProps<T>) {
  const selectedIndex = options.findIndex(o => o.value === value)

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const delta = e.key === 'ArrowRight' ? 1 : -1
    const next = (selectedIndex + delta + options.length) % options.length
    onChange(options[next].value)
    // Roving tabindex: move focus to the newly selected segment so the tab stop follows selection.
    const radios = (e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role="radio"]')
    radios[next]?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={`inline-flex items-center gap-0.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 p-0.5 ${className}`}
    >
      {options.map(option => {
        const isSelected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(option.value)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              isSelected
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            {option.icon}
            {option.label}
            {option.badge}
          </button>
        )
      })}
    </div>
  )
}
